/**
 * Client-side booking shortcut. Renders a SlotPicker reading the specialist's
 * public free slots; on confirm, calls `POST /orders/` with the chosen slot
 * and lands the client in the new per-order chat thread.
 *
 * F-181: client-initiated orders land as `created` and the specialist must
 * confirm via the order_proposal widget in chat. Copy reflects that.
 */
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useHapticFeedback } from '@/hooks';
import { SlotPicker } from './SlotPicker';
import type { Slot } from '@/api/booking';
import type { ServicePreset } from '@/types/domain';
import { createOrder } from '@/api/orders';
import { logger } from '@/utils/logger';

export interface BookSlotModalProps {
  open: boolean;
  onClose: () => void;
  specialistSlug: string;
  preset: ServicePreset;
}

export function BookSlotModal({
  open,
  onClose,
  specialistSlug,
  preset,
}: BookSlotModalProps): JSX.Element | null {
  const navigate = useNavigate();
  const haptic = useHapticFeedback();

  if (!open) return null;

  const onConfirm = async (slot: Slot): Promise<void> => {
    try {
      const order = await createOrder({
        preset_id: preset.id,
        scheduled_at: slot.start_at,
      });
      const chatId = order.chat_id ?? order.id;
      const when = dayjs(slot.start_at).format('D MMM, HH:mm');
      haptic.notification('success');
      toast.success(`Запит на ${when} надіслано — чекаємо підтвердження`);
      onClose();
      navigate(`/chats/${chatId}`);
    } catch (err) {
      logger.warn('createOrder (slot) failed', err);
      toast.error('Не вдалось записати — спробуй ще раз');
    }
  };

  return (
    <Modal open title={`Записатись · ${preset.title}`} onClose={onClose}>
      <p style={{ margin: '0 0 8px', color: 'var(--fg-muted)', fontSize: 13 }}>
        Обери час — майстер підтвердить запис у чаті.
      </p>
      <SlotPicker
        specialistSlug={specialistSlug}
        onConfirm={onConfirm}
        onCancel={onClose}
      />
    </Modal>
  );
}
